import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import Sentry from "@similarweb/sentry-logger";

import type { ICampaignsScrapingConfiguration } from "app/components/campaigns/shared/CampaignsConst";
import { getLocaleForScrapingConfig } from "app/components/campaigns/shared/CampaignsUtils";
import { swNumberFilter } from "app/filters/ngFilters";
import { getCampaignMaxAvailableDate } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContextUtils";
import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type { ISitesForSerpColumn } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import { SERP_FEATURES_MAP } from "app/common/serp/SerpFilterMaps";
import type { SerpFeatureRecordBase } from "app/pages/digital-marketing/keywords-campaigns/reports/serp-feature-report/SERPReportTypes";
import { getValidPositionValue } from "app/pages/digital-marketing/keywords-campaigns/reports/shared/Table/TableUtils/RankTrackerTableSharedUtils";
import type {
    IRankTrackerColumn,
    IRankTrackerColumnToStore,
} from "app/pages/digital-marketing/keywords-campaigns/reports/shared/Table/TableUtils/RankTrackerTablesTypes";
import { fetchSiteInfoQuery, fetchWebsiteImageQuery } from "app/services/sitesService/queries/sitesQueries";
import { getSiteDataFromResponse } from "app/services/sitesService/sitesUtils";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";
import type { IAsset } from "pro-features/components/AutocompleteWithTabs/types";

import type { AntColumnType } from "../../keywords-report/keywords-table/keywordsReportTableContext";

export const getTableColumnsToSave = (
    newColumns: IRankTrackerColumn[] | AntColumnType[],
): IRankTrackerColumnToStore[] => {
    const columnsToSave = newColumns.reduce((acc, col) => {
        const visible = (col as IRankTrackerColumn).visible ?? !(col as AntColumnType).hidden;
        const field = (col as IRankTrackerColumn).key ?? (col as AntColumnType).dataIndex;
        if (!col.groupName) {
            acc.push({ field, visible, isGroup: false });
        } else {
            // merge column groups by group name
            const group = acc.find((group) => group.field === col.groupName);
            if (!group) {
                acc.push({
                    field,
                    visible,
                    isGroup: true,
                    groupName: col.groupName,
                });
            }
        }
        return acc;
    }, []);
    return columnsToSave;
};

export const getURLWithoutPrefix = (url: string): string => {
    let formattedURL = url;
    if (url.startsWith("http://") || url.startsWith("https://")) {
        const urlObj = new URL(url);
        formattedURL = url?.replace(`${urlObj.protocol}//`, "");
    }
    formattedURL = formattedURL?.replace(`www.`, "");
    return formattedURL ? encodeURI(formattedURL) : "";
};

export const getValidCampaignAsset = (requiredAsset: IAsset): IAsset => {
    if (!requiredAsset) {
        return null;
    }
    const formattedDisplayText = getURLWithoutPrefix(requiredAsset?.displayText);
    const formattedId = getURLWithoutPrefix(requiredAsset?.id);
    const asset = {
        ...requiredAsset,
        displayText: formattedDisplayText,
        id: formattedId,
    };
    return asset;
};

export const filterRelevantForCampaignsSERPItems = (records: SerpFeatureRecordBase[]): SerpFeatureRecordBase[] => {
    const filteredRecords = records.filter((record) => SERP_FEATURES_MAP[record.serpFeature] != null);
    return filteredRecords;
};

export const getScrapingConfsIdFromLocales = (
    locationIds: string,
    availableScrapingConfigurations: ICampaignsScrapingConfiguration[],
    delemiter = ",",
): string => {
    return locationIds
        ?.split("&")
        .map((locale) => {
            const scrapingConfId = availableScrapingConfigurations?.find(
                (scrapingConf) => locale === getLocaleForScrapingConfig(scrapingConf),
            );
            return scrapingConfId?.id;
        })
        .join(delemiter);
};

export const getSitesForSerpColumn = (
    currentCampaignConfiguration: IRTCurrentCampaignData,
    selectedSitesIDs: string[],
): Array<ISitesForSerpColumn> => {
    const sitesForSerpColumn: Array<ISitesForSerpColumn> = selectedSitesIDs?.map((site) => {
        const isMainSite = currentCampaignConfiguration?.site === site;
        const color = isMainSite
            ? currentCampaignConfiguration?.color
            : currentCampaignConfiguration?.competitors.find((competitor) => competitor.site === site)?.color;
        return {
            name: site,
            color,
        };
    });
    return sitesForSerpColumn;
};

export const getScrapingConfigsForDashboardConfig = (
    currentCampaignConfiguration: IRTCurrentCampaignData,
    selectedDevice: string,
) => currentCampaignConfiguration?.scrapingConfigurations.filter((config) => selectedDevice?.includes(config.device));

export const getSiteInfo = async (key: string) => {
    try {
        const siteInfo = await fetchSiteInfoQuery({
            keys: key,
        });
        const site = getSiteDataFromResponse(siteInfo, key);
        const siteImage = await fetchWebsiteImageQuery({
            website: key,
        });
        const siteObject: IAsset = {
            displayText: site.displayName,
            image: siteImage.Image as string,
            type: EAssetType.Website,
            id: site.displayName,
        };
        return siteObject;
    } catch (error) {
        console.error("Error in SitesService getSiteInfo:", error);
    }
};

export const isSERPSnapshotButtonEnabled = (
    linkDate: Dayjs,
    lastMigrationDate: Dayjs | null,
    lastScrapeDate: string | null,
) => {
    try {
        const lastAvailableDate = dayjs(getCampaignMaxAvailableDate(lastScrapeDate));
        const minRange = lastAvailableDate.subtract(2, "month");
        const isDateInPastMonth = linkDate.isBetween(minRange, lastAvailableDate, null, "[]");
        const isDateOutOfMigratedData = !lastMigrationDate || linkDate.isAfter(lastMigrationDate);
        return isDateInPastMonth && isDateOutOfMigratedData;
    } catch (error) {
        Sentry.error(`Error fetching table data keywords report - SERP snapshot`, error);
        return false;
    }
};

export function positionNumberFilter() {
    return (val: number | string, decimal?: number, na?: string): string => {
        const formatted = swNumberFilter()(val, decimal, na);
        // Strip commas from formatted values (e.g. "1,000")
        const rounded = Number(String(formatted).replace(/,/g, ""));
        return String(getValidPositionValue(rounded));
    };
}
